import React, { useEffect, useMemo, useRef, useState } from "react";
import { normalizeMatchedProgramChoices } from "./contracts";
import { buildMatchingContext } from "./matchingContext";
import { evaluateRule, meetsEligibility } from "./ruleEvaluator";
import {
  TREATMENT_AUTHORITY_RESPONSE_KEY,
  TREATMENT_RUNTIME_STATE,
} from "./treatmentApiConstants";
import "./matchedProgramsQuestion.css";

const isAvailableInState = (program, state) => {
  if (program.service_states_all) return true;
  if (!state) return true;
  return program.service_states.map(String).map((value) => value.toUpperCase())
    .includes(String(state).toUpperCase());
};

export const buildMatchAuthorityRequestKey = (
  question,
  questionnaire,
  allResponses,
) => {
  const context = buildMatchingContext(questionnaire, allResponses);
  [question.id, question.source_id, question.release_node_id]
    .filter(Boolean)
    .forEach((key) => delete context[String(key)]);
  if (question.matching_config?.stage !== "recommendations") {
    delete context[TREATMENT_AUTHORITY_RESPONSE_KEY.recommendedProgramIds];
  }
  return JSON.stringify(context);
};

export const resolveMatchedPrograms = (question, questionnaire, allResponses) => {
  const context = buildMatchingContext(questionnaire, allResponses);
  const stage = question.matching_config?.stage || "potential";
  const rawInitialSelection = stage === "recommendations"
    ? context[question.matching_config?.initial_selection_question_id]
    : null;
  const initialSelection = stage === "recommendations"
    ? (Array.isArray(rawInitialSelection) ? rawInitialSelection.map(String) : [])
    : null;
  return normalizeMatchedProgramChoices(question).filter((program) =>
    program.matching_enabled !== false &&
    (!initialSelection || initialSelection.includes(program.value)) &&
    evaluateRule(program.matching_rule, context) &&
    meetsEligibility(program.eligibility, context) &&
    isAvailableInState(program, context.service_state) &&
    !program.disqualifying_rules.some((rule) => evaluateRule(rule, context))
  );
};

export const projectAuthoritativeMatches = (question, authoritativeIds) => {
  const ids = authoritativeIds instanceof Set
    ? authoritativeIds
    : new Set((authoritativeIds || []).map(String));
  return normalizeMatchedProgramChoices(question).filter(
    (program) => ids.has(String(program.value)),
  );
};

export const duplicateTreatmentTypeSelections = (question, selectedValues) => {
  const selected = new Set((Array.isArray(selectedValues) ? selectedValues : []).map(String));
  const byTreatmentType = new Map();

  normalizeMatchedProgramChoices(question).forEach((program) => {
    const treatmentTypeId = String(program.treatment_type_id || "");
    if (!treatmentTypeId || !selected.has(String(program.value))) return;
    const entry = byTreatmentType.get(treatmentTypeId) || {
      name: String(program.treatment_type_name || "").trim(),
      programs: [],
    };
    entry.programs.push(program);
    byTreatmentType.set(treatmentTypeId, entry);
  });

  return [...byTreatmentType.values()].filter((entry) => entry.programs.length > 1);
};

export const customProgramSelectionError = (question, selectedValues) => {
  const conflicts = duplicateTreatmentTypeSelections(question, selectedValues);
  if (!conflicts.length) return "";
  if (conflicts.length === 1 && conflicts[0].name) {
    return `Please select only one ${conflicts[0].name} program. These options are alternatives for the same treatment type.`;
  }
  return "Please select only one program for each treatment type. Some selected programs are alternatives for the same treatment.";
};

export default function MatchedProgramsQuestion({
  question,
  value,
  onChange,
  error,
  questionnaire,
  allResponses,
  synchronizeAuthority,
  isCustomProgramJourney = false,
}) {
  const stage = question.matching_config?.stage || "potential";
  const locallyResolvedMatches = useMemo(
    () => resolveMatchedPrograms(question, questionnaire, allResponses),
    [question, questionnaire, allResponses]
  );
  const [authoritativeIds, setAuthoritativeIds] = useState(null);
  const [authorityError, setAuthorityError] = useState("");
  const [authorityLoading, setAuthorityLoading] = useState(
    Boolean(synchronizeAuthority),
  );
  const [authorityAttempt, setAuthorityAttempt] = useState(0);
  const authorityRequestKey = useMemo(
    () => buildMatchAuthorityRequestKey(
      question,
      questionnaire,
      allResponses,
    ),
    [allResponses, question, questionnaire],
  );

  // ponytail: the authority call is re-created by its own hook whenever
  // patientInfo/questionnaire/cursor change, so it must not be an effect
  // dependency -- a re-created identity would cancel the in-flight request
  // and, because the mutation gate returns the same promise for an identical
  // intent, no later settlement would ever clear the loading flag.
  const synchronizeAuthorityRef = useRef(synchronizeAuthority);
  synchronizeAuthorityRef.current = synchronizeAuthority;
  const hasAuthority = Boolean(synchronizeAuthority);

  useEffect(() => {
    if (!hasAuthority) {
      setAuthoritativeIds(null);
      setAuthorityError("");
      setAuthorityLoading(false);
      return undefined;
    }
    let active = true;
    setAuthorityLoading(true);
    setAuthorityError("");
    synchronizeAuthorityRef.current(
      stage === "recommendations"
        ? TREATMENT_RUNTIME_STATE.recommendations
        : TREATMENT_RUNTIME_STATE.potentialMatches,
      allResponses,
    ).then((result) => {
      if (!active) return;
      const ids = stage === "recommendations"
        ? result?.matching?.recommended_programs
        : result?.matching?.potentially_matched;
      setAuthoritativeIds(
        new Set((ids || []).map(String)),
      );
    }).catch((requestError) => {
      if (!active) return;
      setAuthoritativeIds(new Set());
      setAuthorityError(
        requestError.message || "Unable to evaluate treatment matches.",
      );
    }).finally(() => {
      if (active) setAuthorityLoading(false);
    });
    return () => {
      active = false;
    };
  }, [
    authorityRequestKey,
    authorityAttempt,
    stage,
    hasAuthority,
  ]);

  const matches = useMemo(
    () => {
      // A live treatment session is server-authoritative. The browser may
      // render the backend's IDs and let the patient choose among the
      // returned eligible Programs, but it must never turn its local rule
      // evaluation into the final match set. Local evaluation remains only
      // for the legacy/non-authority rendering path.
      if (hasAuthority) {
        if (authoritativeIds === null) return [];
        return projectAuthoritativeMatches(question, authoritativeIds);
      }
      return locallyResolvedMatches;
    },
    [authoritativeIds, hasAuthority, locallyResolvedMatches, question],
  );
  const selected = Array.isArray(value) ? value.map(String) : [];
  const selectionError = isCustomProgramJourney
    ? customProgramSelectionError(question, selected)
    : "";
  const availableIds = useMemo(() => new Set(matches.map((program) => program.value)), [matches]);

  // ponytail: `selected` is rebuilt from `value` on every render, so this
  // effect keys on its content rather than its identity. onChange is read
  // through a ref for the same reason -- the parent re-creates it per render
  // and an identity dependency would re-enter this effect endlessly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const selectedKey = selected.join(",");
  const availableKey = useMemo(
    () => [...availableIds].sort().join(","),
    [availableIds],
  );

  useEffect(() => {
    const current = selectedKey ? selectedKey.split(",") : [];
    const valid = current.filter((programId) => availableIds.has(programId));
    if (valid.length !== current.length) {
      onChangeRef.current(valid);
    } else if (stage === "recommendations" && current.length === 0 && matches.length > 0) {
      onChangeRef.current(matches.map((program) => program.value));
    }
  }, [availableIds, availableKey, selectedKey, matches, stage]);

  const toggle = (programId) => {
    onChange(
      selected.includes(programId)
        ? selected.filter((item) => item !== programId)
        : [...selected, programId]
    );
  };

  const allSelected = matches.length > 0 && matches.every((program) => selected.includes(program.value));
  const toggleAll = () => onChange(allSelected ? [] : matches.map((program) => program.value));

  if (authorityLoading) {
    return (
      <div className="matched-programs-empty" role="status">
        <h3>Finding your treatment matches</h3>
        <p>We’re applying the eligibility and availability rules from this release.</p>
      </div>
    );
  }

  if (authorityError) {
    return (
      <div className="matched-programs-empty" role="alert">
        <h3>We couldn’t evaluate your matches</h3>
        <p>{authorityError}</p>
        <button type="button" onClick={() => setAuthorityAttempt((value) => value + 1)}>
          Try again
        </button>
      </div>
    );
  }

  if (!matches.length) {
    return (
      <div className="matched-programs-empty" role="status">
        <h3>We don't have a matching treatment program yet</h3>
        <p>Your answers did not match an available program. Go back to review them or contact the care team for help.</p>
      </div>
    );
  }

  return (
    <div className="matched-programs" role="group" aria-label={question.question_text}>
      <h2>{stage === "recommendations" ? "Your Matches" : "Based on what you shared"}</h2>
      <p className="matched-programs-helper">
        {stage === "recommendations"
          ? `Based on your screening, ${matches.length === 1 ? "this is the treatment" : "these are the treatments"} our doctors recommend.`
          : `You may be eligible for ${matches.length} treatment${matches.length === 1 ? "" : "s"}. Tap the ones you’d like to be screened for. You can choose one or several — we’ll only run clinical screening for what you pick.`}
      </p>
      <div className="matched-programs-toolbar">
        <span>{selected.filter((id) => availableIds.has(id)).length} of {matches.length} selected</span>
        <button type="button" onClick={toggleAll}>{allSelected ? "Clear all" : "Select all"}</button>
      </div>
      <div className="matched-programs-grid">
        {matches.map((program) => {
          const checked = selected.includes(program.value);
          return (
            <button
              type="button"
              key={program.program_id}
              className={`matched-program-card${checked ? " is-selected" : ""}`}
              aria-pressed={checked}
              onClick={() => toggle(program.value)}
            >
              <span className="matched-program-check" aria-hidden="true">{checked ? "✓" : ""}</span>
              <span className="matched-program-copy">
                <strong>{program.label}</strong>
                {program.treatment_type_name && <small>{program.treatment_type_name}</small>}
                {program.description && <span>{program.description}</span>}
              </span>
              <span className="matched-program-action">{checked ? "Added" : "Tap to add"}</span>
            </button>
          );
        })}
      </div>
      {selected.length === 0 && <div className="matched-programs-warning">Select at least one treatment to continue.</div>}
      {stage === "potential" && <p className="matched-programs-warm-lead">Treatments you don’t pick today stay as warm leads — you can come back anytime to screen for them.</p>}
      {selectionError && <p className="matched-programs-error" role="alert">{selectionError}</p>}
      {error && <p className="matched-programs-error" role="alert">{error}</p>}
    </div>
  );
}
